import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MagicButton from 'components/MagicButton';
import { toast } from 'react-toastify';
import {
  useGetDraftsQuery,
  useGetDraftQuery,
  useGenerateDraftMutation,
  useUpdateDraftStatusMutation,
  DraftStatus,
} from 'store/services/admin/adminSeoApi';
import { formatRelativeTime } from 'utils/formatRelativeTime';
import './DraftsTab.css';

const STATUS_CLASS: Record<DraftStatus, string> = {
  draft: 'dr-status-draft',
  approved: 'dr-status-approved',
  rejected: 'dr-status-rejected',
};

const extractErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === 'object' && 'data' in err) {
    const data = (err as { data?: { message?: string } }).data;
    if (data?.message) return data.message;
  }
  return fallback;
};

const CharCount: React.FC<{ value: string; limit: number }> = ({ value, limit }) => (
  <span className={`dr-char-count ${value.length > limit ? 'dr-char-over' : ''}`}>
    {value.length}/{limit}
  </span>
);

interface Props {
  isActive: boolean;
}

const DraftsTab: React.FC<Props> = ({ isActive }) => {
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data, isLoading, error } = useGetDraftsQuery(undefined, { skip: !isActive });
  const {
    data: draft,
    isLoading: isDraftLoading,
    error: draftError,
  } = useGetDraftQuery(selectedSlug ?? '', { skip: !isActive || !selectedSlug });
  const [generateDraft, { isLoading: isGenerating }] = useGenerateDraftMutation();
  const [updateStatus, { isLoading: isUpdatingStatus }] = useUpdateDraftStatusMutation();

  const handleGenerate = async () => {
    try {
      const result = await generateDraft().unwrap();
      if (result.status === 'skipped') {
        toast.info(result.reason);
        return;
      }
      if (result.status === 'error') {
        toast.error(result.message || 'Failed to generate draft');
        return;
      }
      toast.success(`Draft generated: ${result.title}`);
    } catch (err: unknown) {
      toast.error(extractErrorMessage(err, 'Failed to generate draft'));
    }
  };

  const handleStatusChange = async (status: 'approved' | 'rejected') => {
    if (!draft) return;
    try {
      await updateStatus({ slug: draft.slug, status }).unwrap();
      toast.success(`Draft ${status}`);
    } catch (err: unknown) {
      toast.error(extractErrorMessage(err, `Failed to mark draft as ${status}`));
    }
  };

  const handleCopyMarkdown = async () => {
    if (!draft) return;
    try {
      await navigator.clipboard.writeText(draft.bodyMarkdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  if (isLoading) {
    return <div className="seo-loading">Loading drafts...</div>;
  }

  if (error) {
    return (
      <div className="seo-error-state">
        <h3>Failed to load drafts</h3>
        <p>Please try again later.</p>
      </div>
    );
  }

  // --- Detail view ---
  if (selectedSlug) {
    return (
      <div className="dr-detail">
        <button className="dr-back-link" onClick={() => setSelectedSlug(null)}>
          &larr; Back to drafts
        </button>

        {isDraftLoading ? <div className="seo-loading">Loading draft...</div> : null}

        {draftError ? (
          <div className="seo-error-state">
            <h3>Failed to load draft</h3>
            <p>Please try again later.</p>
          </div>
        ) : null}

        {draft ? (
          <>
            <div className="dr-detail-header">
              <div className="dr-detail-title-row">
                <h3 className="dr-detail-title">{draft.title}</h3>
                <span className={`dr-status-badge ${STATUS_CLASS[draft.status]}`}>
                  {draft.status}
                </span>
              </div>
              <div className="dr-detail-meta-row">
                <span className={`cp-priority-badge cp-${draft.priority.toLowerCase()}`}>
                  {draft.priority}
                </span>
                <span className="dr-detail-keyword">{draft.targetKeyword}</span>
                <span className="dr-detail-info">
                  {draft.estimatedImpressions.toLocaleString()} est. impressions
                </span>
                <span className="dr-detail-info" title={new Date(draft.generatedAt).toLocaleString()}>
                  Generated {formatRelativeTime(draft.generatedAt)}
                </span>
              </div>
              <div className="dr-actions">
                <MagicButton
                  label="Approve"
                  color="purple"
                  onPress={() => handleStatusChange('approved')}
                  isLoading={isUpdatingStatus}
                  isDisabled={isUpdatingStatus || draft.status === 'approved'}
                />
                <MagicButton
                  label="Reject"
                  color="dark-grey"
                  onPress={() => handleStatusChange('rejected')}
                  isLoading={isUpdatingStatus}
                  isDisabled={isUpdatingStatus || draft.status === 'rejected'}
                />
                <MagicButton
                  label={copied ? 'Copied!' : 'Copy Markdown'}
                  color="black"
                  onPress={handleCopyMarkdown}
                />
              </div>
            </div>

            <div className="dr-meta-fields">
              <div className="dr-meta-field">
                <div className="dr-meta-label">
                  Meta Title
                  <CharCount value={draft.metaTitle} limit={60} />
                </div>
                <div className="dr-meta-value">{draft.metaTitle}</div>
              </div>
              <div className="dr-meta-field">
                <div className="dr-meta-label">
                  Meta Description
                  <CharCount value={draft.metaDescription} limit={160} />
                </div>
                <div className="dr-meta-value">{draft.metaDescription}</div>
              </div>
            </div>

            <div className="dr-markdown">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ href, children }) => {
                    const isExternal = href?.startsWith('http');
                    return (
                      <a
                        href={href}
                        target={isExternal ? '_blank' : undefined}
                        rel={isExternal ? 'noopener noreferrer' : undefined}
                      >
                        {children}
                      </a>
                    );
                  },
                }}
              >
                {draft.bodyMarkdown}
              </ReactMarkdown>
            </div>
          </>
        ) : null}
      </div>
    );
  }

  // --- List view ---
  const drafts = data?.drafts ?? [];

  return (
    <>
      <div className="dr-list-header">
        <p className="dr-list-hint">
          One draft is generated automatically each week from the top content plan item.
        </p>
        <MagicButton
          label={isGenerating ? 'Generating...' : 'Generate Draft'}
          color="purple"
          onPress={handleGenerate}
          isLoading={isGenerating}
          isDisabled={isGenerating}
        />
      </div>

      {drafts.length === 0 ? (
        <div className="seo-empty-state">
          <h3>No drafts yet.</h3>
          <p>
            Drafts are generated by the Monday pipeline, or click &quot;Generate Draft&quot; to
            create one now from the current content plan.
          </p>
        </div>
      ) : (
        <div className="dr-table">
          <div className="dr-table-header">
            <span>Title</span>
            <span>Target Keyword</span>
            <span>Status</span>
            <span>Generated</span>
          </div>
          {drafts.map(item => (
            <div
              key={item.slug}
              className="dr-table-row"
              onClick={() => setSelectedSlug(item.slug)}
            >
              <span className="dr-row-title">{item.title}</span>
              <span className="dr-row-keyword">{item.targetKeyword}</span>
              <span>
                <span className={`dr-status-badge ${STATUS_CLASS[item.status]}`}>
                  {item.status}
                </span>
              </span>
              <span className="dr-row-date" title={new Date(item.generatedAt).toLocaleString()}>
                {formatRelativeTime(item.generatedAt)}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default DraftsTab;
